import Vue from 'vue';
import Vuex from 'vuex';
import { ActionTree } from 'vuex';
import { vuexfireMutations, firestoreAction } from 'vuexfire';
import firebase from 'firebase/app';

Vue.use(Vuex);

// import nanoid from 'nanoid';

import auth from '@/plugins/auth';

import { db, Timestamp } from '@/db/firestore';

import { Step } from 'prosemirror-transform';
import schema from '@/components/editor/schema';
const defaultDoc = {
  type: 'doc',
  content: [{ type: 'title', content: [] }, { type: 'paragraph' }],
};

export default new Vuex.Store({
  state: Object.assign(
    {},
    {
      loggedIn: false,
      userInfoRef: null as any,
      user: {
        uid: '',
        draft: '',
        space: null as any,
      },
      space: null,
      localNoteVersion: 0,
      noteRef: null as any,
      note: {
        version: 0,
        doc: null,
        steps: [],
        owners: [],
        lastUpdated: {
          seconds: null as any,
          nanoseconds: null as any,
        },
        createdAt: {
          seconds: null as any,
          nanoseconds: null as any,
        },
      },
      draft: {},
      notes: [],
      pageRef: null as any,
      page: {
        notes: [],
      },
      pages: [],
    },
    {},
  ),
  getters: {
    getNoteById: (state: any) => async (noteid: string) => {
      const docSnapshot = await db
        .collection('notes')
        .doc(noteid)
        .get();
      let doc = docSnapshot.data();
      doc = Object.assign(doc, { id: docSnapshot.id });
      // console.log('doc', doc);
      return doc;
    },
  },
  mutations: {
    auth(state: any, payload: any) {
      state.user = payload.user;
      state.loggedIn = payload.user ? true : false;
    },
    logout(state: any) {
      state.user = {};
      state.loggedIn = false;
    },
    ...vuexfireMutations,
  },
  actions: {
    async auth({ commit, state }, payload): Promise<any> {
      let user: any = await auth.auth();
      if (user === null) {
        return;
      }
      state.userInfoRef = db.collection('users').doc(user.uid);
      const userInfo = await state.userInfoRef.get();
      if (userInfo.exists) {
        user = Object.assign(user, userInfo.data());
      }
      // console.log('user', user);
      commit('auth', { user });
    },
    async logout({ commit, state }, payload): Promise<any> {
      const user = await auth.auth();
      if (user !== null) {
        await firebase.auth().signOut();
      }
      commit('logout');
    },
    async createSpace({ commit, state }, payload): Promise<any> {
      const spaceRef = db.collection('spaces').doc(payload.spaceId);
      // console.log(payload.spaceId, spaceRef);
      const docSnapshot = await spaceRef.get().catch();
      if (docSnapshot.exists) {
        return Promise.reject(new Error('Document already exists'));
      } else {
        // TODO: Require security fix
        await spaceRef.set({
          user: state.userInfoRef,
        });
        await state.userInfoRef.update({
          space: spaceRef,
        });
        state.user.space = payload.space;
        return payload.spaceId;
      }
    },
    bindNotes: firestoreAction(
      async ({ state, bindFirestoreRef }: any, payload) => {
        let ref = null;
        if (
          state.user.space &&
          state.user.space.id === payload.spaceId &&
          state.loggedIn
        ) {
          const userRef = db.collection('users').doc(state.user.uid);
          ref = db
            .collection('notes')
            .where('owners', 'array-contains', userRef);
        } else {
          // NOTE: Space to user
          const spaceSnapshot = await db
            .collection('spaces')
            .doc(payload.spaceId)
            .get();
          const space: any = spaceSnapshot.data();
          const spaceUserRef = space.user;
          ref = db
            .collection('notes')
            .where('sharing', '==', 'public')
            .where('owners', 'array-contains', spaceUserRef);
        }
        return bindFirestoreRef('notes', ref, { maxRefDepth: 0 });
      },
    ),
    bindNote: firestoreAction(({ bindFirestoreRef }, payload) => {
      return bindFirestoreRef('note', db.collection('notes').doc(payload.id), {
        maxRefDepth: 1,
      });
    }),
    bindNoteRef({ commit, state }, payload): void {
      state.noteRef = db.collection('notes').doc(payload.id);
    },
    async bindPages({ commit, state }, payload): Promise<any> {
      const pagesSnapshot = await db
        .collection('spaces')
        .doc(payload.spaceId)
        .collection('pages')
        .get();
      // console.log(pagesSnapshot.docs);
      state.pages = pagesSnapshot.docs;
    },
    bindPage: firestoreAction(({ bindFirestoreRef }, payload) => {
      return bindFirestoreRef(
        'page',
        db
          .collection('spaces')
          .doc(payload.spaceId)
          .collection('pages')
          .doc(payload.pageId),
        { maxRefDepth: 1 },
      );
    }),
    bindPageRef({ commit, state }, payload): void {
      state.pageRef = db
        .collection('spaces')
        .doc(payload.spaceId)
        .collection('pages')
        .doc(payload.pageId);
    },
    async saveNote({ commit, state }: any, payload): Promise<any> {
      const now = new Date();
      if (state.user.draft && state.note.id === state.user.draft.id) {
        const newNote = Object.assign(
          {},
          {
            doc: JSON.stringify(payload.doc),
            sharing: payload.sharing,
            owners: [state.userInfoRef],
            steps: [],
            version: 0,
            createdAt: Timestamp.fromDate(now),
            lastUpdated: Timestamp.fromDate(now),
          },
        );
        await db.collection('notes').add(newNote);
        await db
          .collection('notes')
          .doc(state.user.draft.id)
          .set({
            doc: JSON.stringify(defaultDoc),
            sharing: 'private',
            owners: [state.userInfoRef],
            steps: [],
            version: 0,
          });
      } else {
        await db
          .collection('notes')
          .doc(state.note.id)
          .update({
            sharing: payload.sharing,
            lastUpdated: Timestamp.fromDate(now),
          });
      }
    },
    async removeNote({ commit, state }: any, payload): Promise<any> {
      // TODO: Fix
      if (state.note.id !== state.user.draft.id) {
        await db
          .collection('notes')
          .doc(state.note.id)
          .delete();
      }
    },
    transactionalUpdateNote: firestoreAction(
      async ({ state }: any, payload) => {
        await db
          .runTransaction(async (transaction) => {
            const noteRef = await transaction.get(state.noteRef);
            const note: any = noteRef.data();
            if (payload.version < note.version) {
              transaction.update(state.noteRef, {
                // version: note.version,
                // steps: note.steps,
              });
              return;
            }
            const newSteps = payload.steps.map((step: any, index: number) => {
              const newStep = Step.fromJSON(schema, step);
              return {
                step: JSON.stringify(newStep.toJSON()),
                version: payload.version + (index + 1),
                clientID: payload.clientID,
              };
            });
            const newVersion = note.version + newSteps.length;
            transaction.update(state.noteRef, {
              version: newVersion,
              steps: [...note.steps.splice(-100), ...newSteps],
              doc: JSON.stringify(payload.doc),
            });
          })
          .catch((err) => {
            // console.log('Transaction failed: ', err);
          });
      },
    ),
    async addPage({ commit, state }: any, payload): Promise<any> {
      return;
    },
    async addNoteToPage({ commit, state }: any, payload): Promise<any> {
      const noteRef = db.collection('notes').doc(payload.noteId);
      const page = await state.pageRef.get();
      if (!page.exists) {
        await state.pageRef.set({
          notes: [noteRef],
        });
      } else {
        await state.pageRef.update({
          notes: firebase.firestore.FieldValue.arrayUnion(noteRef),
        });
      }
    },
    async removeNoteFromPage({ commit, state }: any, payload): Promise<any> {
      const noteRef = db.collection('notes').doc(payload.noteId);
      await state.pageRef.update({
        notes: firebase.firestore.FieldValue.arrayRemove(noteRef),
      });
      if (state.page.notes.length === 0) {
        await state.pageRef.delete();
      }
    },
  } as ActionTree<any, any>,
});
